import React, { useState, useEffect } from "react";
import { Drawer, Form, Input, Select, Button, Switch, Collapse, message } from "antd";
import { UpOutlined, DownOutlined, CloseOutlined, PlusOutlined } from "@ant-design/icons";
import {
  createEventMapping,
  updateEventMapping,
  getEventCapabilities,
} from "../../../services/integrations/klaviyoEventMappingsService";

const { Option } = Select;
const { Panel } = Collapse;

const emptyStep = () => ({
  actionType: "EMIT_EVENT",
  vendorActionName: "",
  payloadSpec: [{ key: "", value: "" }],
  providerField: "",
  valuePath: "",
});

export default function EventMappingDrawer({ visible, onClose, onSaved, editMapping }) {
  const [form] = Form.useForm();
  const [capabilities, setCapabilities] = useState([]);
  const [selectedCapability, setSelectedCapability] = useState(null);
  const [steps, setSteps] = useState([emptyStep()]);
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const isEdit = !!editMapping;

  useEffect(() => {
    if (visible) {
      loadCapabilities();
      if (editMapping) {
        form.setFieldsValue({
          bluestreamEventType: editMapping.bluestreamEventType,
          name: editMapping.name,
        });
        setEnabled(editMapping.enabled !== false);
        if (editMapping.steps?.length > 0) {
          setSteps(editMapping.steps.map((s) => ({
            actionType: s.actionType || "EMIT_EVENT",
            vendorActionName: s.vendorActionName || "",
            payloadSpec: s.payloadSpec
              ? Object.entries(s.payloadSpec).map(([key, value]) => ({ key, value }))
              : [{ key: "", value: "" }],
            providerField: s.providerField || "",
            valuePath: s.valuePath || "",
          })));
        } else {
          setSteps([emptyStep()]);
        }
      } else {
        form.resetFields();
        setSteps([emptyStep()]);
        setEnabled(true);
        setSelectedCapability(null);
      }
    }
  }, [visible, editMapping, form]);

  const loadCapabilities = async () => {
    try {
      const res = await getEventCapabilities();
      setCapabilities(Array.isArray(res) ? res : res?.capabilities || []);
    } catch (err) {
      // capabilities may not be available
    }
  };

  const handleEventTypeChange = (value) => {
    const cap = capabilities.find((c) => c.id === value || c.eventType === value);
    setSelectedCapability(cap || null);
  };

  const updateStep = (index, field, value) => {
    setSteps((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addStep = () => {
    setSteps((prev) => [...prev, emptyStep()]);
  };

  const removeStep = (index) => {
    setSteps((prev) => prev.filter((_, i) => i !== index));
  };

  const moveStep = (index, direction) => {
    setSteps((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return next;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const updatePayloadField = (stepIndex, fieldIndex, prop, value) => {
    setSteps((prev) => {
      const next = [...prev];
      const spec = [...next[stepIndex].payloadSpec];
      spec[fieldIndex] = { ...spec[fieldIndex], [prop]: value };
      next[stepIndex] = { ...next[stepIndex], payloadSpec: spec };
      return next;
    });
  };

  const addPayloadField = (stepIndex) => {
    setSteps((prev) => {
      const next = [...prev];
      next[stepIndex] = {
        ...next[stepIndex],
        payloadSpec: [...next[stepIndex].payloadSpec, { key: "", value: "" }],
      };
      return next;
    });
  };

  const removePayloadField = (stepIndex, fieldIndex) => {
    setSteps((prev) => {
      const next = [...prev];
      next[stepIndex] = {
        ...next[stepIndex],
        payloadSpec: next[stepIndex].payloadSpec.filter((_, i) => i !== fieldIndex),
      };
      return next;
    });
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const mappingSteps = steps.map((s) => {
        if (s.actionType === "EMIT_EVENT") {
          const spec = {};
          s.payloadSpec.forEach((row) => {
            if (row.key.trim()) spec[row.key.trim()] = row.value;
          });
          return {
            actionType: s.actionType,
            vendorActionName: s.vendorActionName,
            payloadSpec: spec,
          };
        }
        return {
          actionType: s.actionType,
          providerField: s.providerField,
          valuePath: s.valuePath,
        };
      });

      const body = {
        bluestreamEventType: values.bluestreamEventType,
        name: values.name || null,
        enabled,
        steps: mappingSteps,
      };

      if (isEdit) {
        await updateEventMapping(editMapping.id, body);
        message.success("Event mapping updated");
      } else {
        await createEventMapping(body);
        message.success("Event mapping created");
      }
      onSaved();
      onClose();
    } catch (err) {
      if (err?.errorFields) return; // form validation
      message.error("Failed to save event mapping");
    }
    setSaving(false);
  };

  const payloadFields = selectedCapability?.payloadFields || selectedCapability?.payload_fields || [];

  return (
    <Drawer
      title={isEdit ? "Edit Event Mapping" : "Add Event Mapping"}
      placement="right"
      width={600}
      open={visible}
      onClose={onClose}
      footer={
        <div className="drawer-footer" style={{ position: "relative" }}>
          <Button className="standard-button secondary-orange-button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="standard-button primary-orange-button"
            loading={saving}
            onClick={handleSave}
          >
            Save
          </Button>
        </div>
      }
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="Trigger Event"
          name="bluestreamEventType"
          rules={[{ required: true, message: "Select a trigger event" }]}
        >
          <Select
            placeholder="Select event type"
            className="standard-input"
            disabled={isEdit}
            onChange={handleEventTypeChange}
            showSearch
            optionFilterProp="children"
          >
            {capabilities.map((cap) => (
              <Option key={cap.id || cap.eventType} value={cap.id || cap.eventType}>
                {cap.description || cap.eventType} ({cap.id || cap.eventType})
              </Option>
            ))}
          </Select>
        </Form.Item>

        {payloadFields.length > 0 && (
          <div className="payload-fields-panel">
            <Collapse ghost>
              <Panel header="Available Payload Fields" key="fields">
                <div className="field-list">
                  {payloadFields.map((field) => (
                    <span key={field.name || field} className="field-tag">
                      {field.name || field}
                    </span>
                  ))}
                </div>
              </Panel>
            </Collapse>
          </div>
        )}

        <Form.Item label="Mapping Name" name="name">
          <Input
            className="standard-input"
            placeholder={`When ${form.getFieldValue("bluestreamEventType") || "{eventType}"} fires`}
          />
        </Form.Item>
      </Form>

      <div className="steps-section-heading">Steps</div>

      {steps.map((step, index) => (
        <div key={index} className="step-card">
          <div className="step-card-header">
            <span className="step-badge">{index + 1}</span>
            <div className="step-actions">
              <Button
                size="small"
                icon={<UpOutlined />}
                disabled={index === 0}
                onClick={() => moveStep(index, -1)}
              />
              <Button
                size="small"
                icon={<DownOutlined />}
                disabled={index === steps.length - 1}
                onClick={() => moveStep(index, 1)}
              />
              {steps.length > 1 && (
                <Button
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={() => removeStep(index)}
                />
              )}
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 13, fontWeight: 500 }}>Action Type</label>
            <Select
              value={step.actionType}
              onChange={(val) => updateStep(index, "actionType", val)}
              style={{ width: "100%", marginTop: 4 }}
              className="standard-input"
            >
              <Option value="EMIT_EVENT">EMIT_EVENT</Option>
              <Option value="WRITE_ATTRIBUTE">WRITE_ATTRIBUTE</Option>
            </Select>
          </div>

          {step.actionType === "EMIT_EVENT" && (
            <>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 13, fontWeight: 500 }}>Vendor Action Name</label>
                <Input
                  className="standard-input"
                  value={step.vendorActionName}
                  onChange={(e) => updateStep(index, "vendorActionName", e.target.value)}
                  placeholder="e.g., Polly Conversation Closed"
                  style={{ marginTop: 4 }}
                />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500 }}>Payload Spec</label>
                {step.payloadSpec.map((row, fi) => (
                  <div key={fi} className="payload-row" style={{ marginTop: fi === 0 ? 4 : 0 }}>
                    <Input
                      className="standard-input"
                      placeholder="Key"
                      value={row.key}
                      onChange={(e) => updatePayloadField(index, fi, "key", e.target.value)}
                    />
                    <Input
                      className="standard-input"
                      placeholder="Value / JSONPath"
                      value={row.value}
                      onChange={(e) => updatePayloadField(index, fi, "value", e.target.value)}
                    />
                    {step.payloadSpec.length > 1 && (
                      <Button
                        size="small"
                        icon={<CloseOutlined />}
                        onClick={() => removePayloadField(index, fi)}
                      />
                    )}
                  </div>
                ))}
                <Button
                  type="link"
                  icon={<PlusOutlined />}
                  onClick={() => addPayloadField(index)}
                  style={{ paddingLeft: 0, marginTop: 4 }}
                >
                  Add Field
                </Button>
              </div>
            </>
          )}

          {step.actionType === "WRITE_ATTRIBUTE" && (
            <>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 13, fontWeight: 500 }}>Provider Field</label>
                <Input
                  className="standard-input"
                  value={step.providerField}
                  onChange={(e) => updateStep(index, "providerField", e.target.value)}
                  placeholder="Klaviyo profile property name"
                  style={{ marginTop: 4 }}
                />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 500 }}>Value Path</label>
                <Input
                  className="standard-input"
                  value={step.valuePath}
                  onChange={(e) => updateStep(index, "valuePath", e.target.value)}
                  placeholder="e.g., $.sentiment_overall"
                  style={{ marginTop: 4 }}
                />
              </div>
            </>
          )}
        </div>
      ))}

      <Button type="dashed" block icon={<PlusOutlined />} onClick={addStep}>
        Add Step
      </Button>

      <div style={{ marginTop: 20, display: "flex", alignItems: "center", gap: 12 }}>
        <label style={{ fontSize: 13, fontWeight: 500 }}>Enabled</label>
        <Switch checked={enabled} onChange={setEnabled} />
      </div>
    </Drawer>
  );
}
